/**
 * API Configuration
 *
 * Centralized configuration for API endpoints.
 * Base URL must always be a full absolute URL (with protocol) so fetch() does not treat it as relative.
 */

// Backend runs on this port (see server/index.js and package.json "server" script)
const BACKEND_PORT = process.env.REACT_APP_API_PORT || '8080';

/** Ensure URL has protocol so it is never treated as a relative path */
function ensureAbsoluteUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const trimmed = url.trim().replace(/\/+$/, '');
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `http://${trimmed}`;
}

/** Get API base URL (always absolute in production; empty = same-origin + CRA proxy in local dev). */
export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return ensureAbsoluteUrl(process.env.REACT_APP_API_URL);
  }

  // Local dev in browser: relative /api/* → CRA proxy (package.json) → backend :8080.
  // Keeps ydim_session on the SPA origin so Set-Cookie + credentialed POST/GET work.
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return '';
    }
  }

  if (process.env.NODE_ENV === 'development') {
    return `http://localhost:${BACKEND_PORT}`;
  }

  return 'https://api.yimnakai.com';
};

// Resolved once at module load (browser dev → ''; production host → api.yimnakai.com).
export const API_BASE_URL = getApiBaseUrl();

// API endpoints (paths only, always start with /)
export const API_ENDPOINTS = {
  REGISTER: '/api/auth/register',
  LOGIN: '/api/auth/login',
  LOGIN_OTP_CONFIG: '/api/auth/login/otp/config',
  LOGIN_OTP_SEND: '/api/auth/login/otp/send',
  LOGIN_OTP_VERIFY: '/api/auth/login/otp/verify',
  AUTH_ME: '/api/auth/me',
  AUTH_LOGOUT: '/api/auth/logout',
  AUTH_GUEST: '/api/auth/guest',
  FORGOT_PASSWORD: '/api/auth/forgot-password',
  RESET_PASSWORD: '/api/auth/reset-password',
  AUTH_FIRM: '/api/auth/firm',
  FIRM_ME: '/api/firm/me',
  FIRM_CASES: '/api/firm/cases',
  FIRM_CASE_BY_ID: (id) => `/api/firm/cases/${id}`,
  FIRM_DOCUMENTS: '/api/firm/documents',
  CHAT_SEND: '/api/chat/send',
  CONTACT_SEND: '/api/contact/send',
  /** Public contact form (Yimnak website inquiries → email) */
  CONTACT_INQUIRY: '/api/contact-inquiry',
  PLEADINGS_SEND: '/api/pleadings/send',
  PLEADINGS_JOB: (jobId) => `/api/pleadings/jobs/${encodeURIComponent(jobId)}`,
  TRACK_VISIT: '/api/track/visit',
  TRACK_INTERACTION: '/api/track/interaction',
  ADMIN_SUMMARY: '/api/admin/summary',
  ADMIN_INTERACTIONS: '/api/admin/interactions',
  /** Internal analytics: admin session + YDIM_ADMIN_EMAILS (server) */
  ADMIN_ANALYTICS: '/api/admin/analytics',
  /** Read-only summary from the Ingestion DB (separate from Ydim analytics) */
  ADMIN_INGESTION_ANALYTICS: '/api/admin/ingestion-analytics',
  /** Read-only summary from Legal Intelligence Engine API */
  ADMIN_LEGAL_INTELLIGENCE_ANALYTICS: '/api/admin/legal-intelligence-analytics',
  /** Public track endpoint; never throws on client */
  ANALYTICS_TRACK: '/api/analytics/track',
  ADMIN_BILLING_SETTINGS: '/api/admin/settings/billing',
  ADMIN_AUTH_LOGIN_SETTINGS: '/api/admin/settings/auth-login',
  /** Sprint 3.1 — commercial readiness (read-only) */
  ADMIN_COMMERCIAL_PLAN_CATALOG: '/api/admin/commercial/plan-catalog',
  ADMIN_COMMERCIAL_EFFECTIVE_PLAN: (userId) => `/api/admin/commercial/effective-plan/${encodeURIComponent(userId)}`,
  ADMIN_COMMERCIAL_GUEST_POLICY: '/api/admin/commercial/guest-policy',
  ADMIN_COMMERCIAL_FIRM_ENTITLEMENT_PREVIEW: '/api/admin/commercial/firm-entitlement-preview',
  ADMIN_COMMERCIAL_AI_ALLOWANCE_PREVIEW: '/api/admin/commercial/ai-allowance-preview',
  ADMIN_COMMERCIAL_ENFORCEMENT_READINESS: '/api/admin/commercial/enforcement-readiness',
  /** Sprint 3.7 — commercial orchestration observability (read-only) */
  ADMIN_COMMERCIAL_EVENTS_RECENT: '/api/admin/commercial/events-recent',
  ADMIN_COMMERCIAL_ORCHESTRATION_HEALTH: '/api/admin/commercial/orchestration-health',
  /** Sprint 3.8 — shadow readiness bundle (read-only) */
  ADMIN_READINESS_SHADOW_INFRASTRUCTURE: '/api/admin/readiness/shadow-infrastructure',
  /** Sprint 3.1.5 — operational & platform (read-only) */
  ADMIN_OPERATIONAL_INSIGHTS: '/api/admin/operational/insights',
  ADMIN_OPERATIONAL_HEALTH: '/api/admin/operational/health',
  ADMIN_OPERATIONAL_ASYNC_SUMMARY: '/api/admin/operational/async-summary',
  ADMIN_OPERATIONAL_ASYNC_RECENT: '/api/admin/operational/async-recent',
  ADMIN_PLATFORM_READINESS: '/api/admin/platform/readiness',
  ADMIN_OBSERVABILITY: '/api/admin/observability',
  ADMIN_SETTINGS_COMMERCIAL_DESCRIPTOR: '/api/admin/settings/commercial-descriptor',
  USERS_UPSERT: '/api/users/upsert',
  SUBSCRIPTION_CURRENT: '/api/subscription/current',
  SUBSCRIPTION_UPGRADE: '/api/subscription/upgrade',
  BILLING_CHECKOUT: '/api/billing/checkout',
  SUBSCRIPTION_USAGE: '/api/subscription/usage',
  SUBSCRIPTION_CANCEL: '/api/subscription/cancel',
  HEALTH: '/api/health',
};

/**
 * Build full API URL from base and path. Avoids double slashes and relative paths.
 * @param {string} path - Path (e.g. API_ENDPOINTS.FORGOT_PASSWORD), must start with /
 * @param {string} [base] - Optional base URL; defaults to API_BASE_URL
 * @returns {string} Full absolute URL
 */
export const buildApiUrl = (path, base = getApiBaseUrl()) => {
  const b = (base || '').trim().replace(/\/+$/, '');
  const p = (path || '').trim();
  const pathWithSlash = p.startsWith('/') ? p : `/${p}`;
  if (!b) return pathWithSlash;
  return `${b}${pathWithSlash}`;
};

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API Configuration:');
  console.log('  Base URL:', API_BASE_URL);
  console.log('  Environment:', process.env.NODE_ENV);
}

export default {
  API_BASE_URL,
  API_ENDPOINTS,
  buildApiUrl,
  getApiBaseUrl
};
